// Deterministic financial calculations. All values derive from imported or user-entered data.
import { monthKey, round2, addDays, daysBetween, today, parseISO, toISO, rateLabel } from "./format";
import { merchantKey, merchantName } from "./categorize";

export const catMapOf = (state) => Object.fromEntries(state.categories.map((c) => [c.id, c]));
export const cardModeOf = (state) => {
  const cardIds = new Set(state.accounts.filter((a) => a.type === "credit").map((a) => a.id));
  return state.transactions.some((t) => cardIds.has(t.accountId));
};
export const txParts = (tx) => (tx.splits && tx.splits.length ? tx.splits : [{ category: tx.category, amount: tx.amount }]);
export const txKey = (t) => `${t.accountId}|${t.date}|${round2(t.amount)}|${merchantKey(t.description)}`;

export function partKind(tx, part, cats, cardMode) {
  if (tx.excluded) return "excluded";
  const c = cats[part.category];
  if (!c) return part.amount > 0 ? "income" : "expense";
  if (c.type === "transfer") return "transfer";
  if (c.id === "credit-card-payments" && cardMode) return "transfer";
  return c.type;
}

const blank = () => ({ income: 0, expense: 0, savings: 0, fixed: 0, variable: 0, essential: 0, discretionary: 0, debt: 0, business: 0, transfers: 0, byCat: {}, incomeByCat: {}, savingsByCat: {}, count: 0 });

export function filterTx(state, f = {}) {
  return state.transactions.filter(
    (t) =>
      (!f.accountId || f.accountId === "all" || t.accountId === f.accountId) &&
      (!f.memberId || f.memberId === "all" || t.memberId === f.memberId) &&
      (!f.scope || f.scope === "all" || (t.scope || "shared") === f.scope)
  );
}

export function computeMonths(state, f = {}) {
  const cats = catMapOf(state);
  const cardMode = cardModeOf(state);
  const acctType = Object.fromEntries(state.accounts.map((a) => [a.id, a.type]));
  const months = {};
  for (const tx of filterTx(state, f)) {
    if (!tx.date) continue;
    const s = (months[monthKey(tx.date)] ||= blank());
    s.count += 1;
    for (const p of txParts(tx)) {
      const kind = partKind(tx, p, cats, cardMode);
      const c = cats[p.category];
      if (kind === "income") {
        s.income += p.amount;
        s.incomeByCat[p.category] = (s.incomeByCat[p.category] || 0) + p.amount;
      } else if (kind === "expense") {
        const spend = -p.amount;
        s.expense += spend;
        s.byCat[p.category] = (s.byCat[p.category] || 0) + spend;
        if (c?.fixed) s.fixed += spend;
        else s.variable += spend;
        if (c?.essential) s.essential += spend;
        else s.discretionary += spend;
        if (c?.debt) s.debt += spend;
        if (tx.business) s.business += spend;
      } else if (kind === "savings") {
        const v = acctType[tx.accountId] === "savings" ? p.amount : -p.amount;
        s.savings += v;
        s.savingsByCat[p.category] = (s.savingsByCat[p.category] || 0) + v;
      } else s.transfers += Math.abs(p.amount);
    }
  }
  Object.values(months).forEach((s) => {
    ["income", "expense", "savings", "fixed", "variable", "essential", "discretionary", "debt", "business", "transfers"].forEach((k) => (s[k] = round2(s[k])));
    s.remaining = round2(s.income - s.expense);
    s.net = round2(s.income - s.expense - s.savings);
    s.savingsRate = s.income > 0 ? round2((s.savings / s.income) * 100) : 0;
  });
  return months;
}

export const sortedMonths = (months) => Object.keys(months).sort();
const ceil5 = (n) => (n <= 0 ? 0 : Math.ceil(n / 5) * 5);

export function budgetModel(state, windowMonths) {
  const months = computeMonths(state);
  const keys = sortedMonths(months);
  const win = windowMonths || state.settings.avgWindow || 3;
  const used = keys.slice(-win);
  const n = used.length || 1;
  const cats = state.categories;
  const manual = state.budget.manual || [];
  const rows = cats
    .filter((c) => c.type !== "transfer")
    .map((c) => {
      const field = c.type === "income" ? "incomeByCat" : c.type === "savings" ? "savingsByCat" : "byCat";
      const observed = round2(used.reduce((s, k) => s + (months[k]?.[field]?.[c.id] || 0), 0) / n);
      const estimated = round2(manual.filter((m) => m.category === c.id).reduce((s, m) => s + Number(m.amount || 0), 0));
      const suggested = c.type === "income" ? round2(observed + estimated) : ceil5(observed + estimated);
      const userLimit = state.budget.limits?.[c.id];
      const limit = userLimit !== undefined && userLimit !== null && userLimit !== "" ? Number(userLimit) : suggested;
      return { ...c, observed, estimated, suggested, limit, custom: userLimit !== undefined && userLimit !== null && userLimit !== "" };
    });
  const sum = (pred, field = "limit") => round2(rows.filter(pred).reduce((s, r) => s + r[field], 0));
  const exp = (r) => r.type === "expense";
  const income = sum((r) => r.type === "income");
  const expenses = sum(exp);
  const savings = sum((r) => r.type === "savings");
  const essential = sum((r) => exp(r) && r.essential);
  const summary = {
    income,
    expenses,
    fixed: sum((r) => exp(r) && r.fixed),
    variable: sum((r) => exp(r) && !r.fixed),
    essential,
    discretionary: sum((r) => exp(r) && !r.essential),
    debt: sum((r) => exp(r) && r.debt),
    savings,
    net: round2(income - expenses - savings),
    availableDiscretionary: round2(income - essential - savings),
    observedIncome: sum((r) => r.type === "income", "observed"),
    observedExpenses: sum(exp, "observed"),
  };
  return { rows, summary, monthsUsed: used, monthCount: used.length, totalMonths: keys.length, preliminary: used.length < 3, months };
}

export function budgetVsActual(state, month, model) {
  const m = model || budgetModel(state);
  const months = m.months;
  const keys = sortedMonths(months);
  const prev = keys[keys.indexOf(month) - 1];
  return m.rows
    .filter((r) => r.type === "expense" && (r.limit > 0 || months[month]?.byCat?.[r.id]))
    .map((r) => {
      const actual = round2(months[month]?.byCat?.[r.id] || 0);
      const prevActual = prev ? months[prev]?.byCat?.[r.id] || 0 : 0;
      const rollover = state.budget.rollover?.[r.id] && prev ? round2(Math.max(0, r.limit - prevActual)) : 0;
      const available = round2(r.limit + rollover);
      return { id: r.id, name: r.name, group: r.group, limit: r.limit, rollover, available, actual, remaining: round2(available - actual), pct: available > 0 ? (actual / available) * 100 : actual > 0 ? 100 : 0 };
    })
    .sort((a, b) => b.actual - a.actual);
}

const FREQS = [
  ["weekly", 6, 8, 7],
  ["biweekly", 12, 16, 14],
  ["semi-monthly", 14, 17, 15],
  ["monthly", 26, 35, 30],
  ["quarterly", 84, 98, 91],
  ["annual", 350, 380, 365],
];
export const freqDays = (f) => (FREQS.find((x) => x[0] === f) || [0, 0, 0, 30])[3];
export const monthlyEquivalent = (amount, freq) => round2((Math.abs(amount) * 30.44) / freqDays(freq));

export function detectRecurring(state) {
  const cats = catMapOf(state);
  const cardMode = cardModeOf(state);
  const groups = {};
  state.transactions.forEach((t) => {
    if (!t.date || t.amount === 0) return;
    const kind = partKind(t, { category: t.category, amount: t.amount }, cats, cardMode);
    if (kind === "transfer" || kind === "excluded") return;
    const k = `${merchantKey(t.description)}|${t.amount > 0 ? "in" : "out"}`;
    (groups[k] ||= []).push(t);
  });
  const out = [];
  Object.entries(groups).forEach(([k, list]) => {
    list.sort((a, b) => a.date.localeCompare(b.date));
    const cat = cats[list[list.length - 1].category];
    const isIncome = k.endsWith("|in");
    const amounts = list.map((t) => Math.abs(t.amount));
    const last = list[list.length - 1];
    const base = { key: k, name: merchantName(last.description), category: last.category, categoryName: cat?.name || "", isIncome, lastDate: last.date, amount: Math.abs(last.amount), count: list.length };
    if (list.length >= 2) {
      const gaps = list.slice(1).map((t, i) => daysBetween(list[i].date, t.date)).filter((g) => g > 3);
      if (!gaps.length) return;
      const med = gaps.sort((a, b) => a - b)[Math.floor(gaps.length / 2)];
      let f = FREQS.find(([, lo, hi]) => med >= lo && med <= hi);
      const doms = [...new Set(list.slice(-4).map((t) => Number(t.date.slice(8, 10))))].sort((a, b) => a - b);
      let days = null;
      if (med >= 12 && med <= 17 && doms.length === 2 && list.length >= 3) {
        f = FREQS.find((x) => x[0] === "semi-monthly");
        days = doms;
      }
      if (!f) return;
      const min = Math.min(...amounts);
      const max = Math.max(...amounts);
      const tolerance = cat?.group === "Utilities" || isIncome ? 1.8 : 1.3;
      if (max / Math.max(min, 0.01) > tolerance) return;
      const prevAvg = amounts.slice(0, -1).reduce((s, a) => s + a, 0) / (amounts.length - 1);
      const increase = !isIncome && base.amount > prevAvg * 1.05 && base.amount - prevAvg >= 1 ? { from: round2(prevAvg), to: base.amount, pct: round2(((base.amount - prevAvg) / prevAvg) * 100) } : null;
      out.push({ ...base, frequency: f[0], days, avgAmount: round2(amounts.reduce((s, a) => s + a, 0) / amounts.length), nextDate: nextOccurrence(last.date, f[0], days), confidence: list.length >= 3 ? "high" : "medium", increase });
    } else if (!isIncome && cat && (cat.fixed || cat.id === "subscriptions") && !cat.debt) {
      out.push({ ...base, frequency: "monthly", avgAmount: base.amount, nextDate: nextOccurrence(last.date, "monthly"), confidence: "low", increase: null });
    }
  });
  return out.sort((a, b) => b.amount - a.amount);
}

// Combine user bills with detections (not dismissed, not already saved)
export function billList(state) {
  const detected = detectRecurring(state).filter((d) => !(state.dismissedBills || []).includes(d.key));
  const saved = state.bills.map((b) => {
    const d = detected.find((x) => x.key === b.key);
    return { ...b, detected: d || null, nextDate: rollForward(b.nextDate, b.frequency, b.days), increase: d?.increase || null };
  });
  const savedKeys = new Set(state.bills.map((b) => b.key).filter(Boolean));
  const suggestions = detected.filter((d) => !savedKeys.has(d.key));
  return { saved, suggestions };
}

function addMonths(s, n) {
  const d = parseISO(s);
  const t = new Date(d.getFullYear(), d.getMonth() + n, 1);
  const last = new Date(t.getFullYear(), t.getMonth() + 1, 0).getDate();
  t.setDate(Math.min(d.getDate(), last));
  return toISO(t);
}

export function nextOccurrence(date, freq, days) {
  if (freq === "monthly") return addMonths(date, 1);
  if (freq === "quarterly") return addMonths(date, 3);
  if (freq === "annual") return addMonths(date, 12);
  if (freq === "semi-monthly" && days?.length === 2) {
    const d = parseISO(date);
    const lastDay = (y, m) => new Date(y, m + 1, 0).getDate();
    const second = Math.min(days[1], lastDay(d.getFullYear(), d.getMonth()));
    if (d.getDate() < second) return toISO(new Date(d.getFullYear(), d.getMonth(), second));
    const nm = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    return toISO(new Date(nm.getFullYear(), nm.getMonth(), Math.min(days[0], lastDay(nm.getFullYear(), nm.getMonth()))));
  }
  return addDays(date, freqDays(freq));
}

export function rollForward(date, freq, days, from = today()) {
  if (!date) return date;
  let d = date;
  let guard = 0;
  while (d < from && guard++ < 1000) d = nextOccurrence(d, freq, days);
  return d;
}

export function projectRecurring(state, start, end) {
  const { saved, suggestions } = billList(state);
  const moves = state.billMoves || [];
  const pStart = addDays(start, -45);
  const pEnd = addDays(end, 45);
  const events = [];
  const push = (item, confirmed) => {
    const itemKey = item.id || item.key;
    let d = rollForward(item.nextDate, item.frequency, item.days, pStart);
    let guard = 0;
    while (d && d <= pEnd && guard++ < 400) {
      const mv = moves.find((m) => m.itemKey === itemKey && m.from === d);
      events.push({ date: mv ? mv.to : d, originalDate: d, moved: !!mv, itemKey, name: item.name, amount: item.isIncome ? Math.abs(item.amount) : -Math.abs(item.amount), confirmed, isIncome: !!item.isIncome, category: item.category });
      d = nextOccurrence(d, item.frequency, item.days);
    }
    (item.extraDates || []).filter((x) => x >= pStart && x <= pEnd).forEach((x) => events.push({ date: x, originalDate: x, moved: false, itemKey, name: item.name, amount: item.isIncome ? Math.abs(item.amount) : -Math.abs(item.amount), confirmed, isIncome: !!item.isIncome, category: item.category }));
  };
  saved.filter((b) => b.active !== false).forEach((b) => push(b, true));
  suggestions.filter((s) => s.confidence !== "low").forEach((s) => push(s, false));
  return { events: events.filter((e) => e.date >= start && e.date <= end && e.originalDate >= (e.moved ? pStart : start)).sort((a, b) => a.date.localeCompare(b.date)), saved, suggestions };
}

export function liquidBalance(state) {
  const liquid = state.accounts.filter((a) => a.type !== "credit");
  const known = liquid.filter((a) => a.balance !== null && a.balance !== undefined && a.balance !== "");
  return { total: round2(known.reduce((s, a) => s + Number(a.balance), 0)), known: known.length, accounts: liquid.length };
}

export function forecast(state, days = 90) {
  const start = today();
  const { events, saved, suggestions } = projectRecurring(state, start, addDays(start, days));

  // Variable (non-recurring) spending from history, spread daily
  const months = computeMonths(state);
  const keys = sortedMonths(months).slice(-(state.settings.avgWindow || 3));
  const avgExpense = keys.length ? keys.reduce((s, k) => s + months[k].expense, 0) / keys.length : 0;
  const recurringMonthly = [...saved.filter((b) => !b.isIncome && b.active !== false), ...suggestions.filter((s) => !s.isIncome && s.confidence !== "low")].reduce((s, b) => s + monthlyEquivalent(b.amount, b.frequency), 0);
  const variableMonthly = Math.max(0, round2(avgExpense - recurringMonthly));
  const dailyVar = variableMonthly / 30.44;
  const bal = liquidBalance(state);
  const threshold = Number(state.settings.lowBalance || 0);
  const series = [];
  let balance = bal.total;
  let shortage = null;
  for (let i = 0; i <= days; i++) {
    const d = addDays(start, i);
    const todays = events.filter((e) => e.date === d);
    if (i > 0) balance -= dailyVar;
    todays.forEach((e) => (balance += e.amount));
    balance = round2(balance);
    if (!shortage && balance < threshold) shortage = { date: d, balance };
    series.push({ date: d, balance, confirmed: todays.filter((e) => e.confirmed).reduce((s, e) => s + e.amount, 0), predicted: todays.filter((e) => !e.confirmed).reduce((s, e) => s + e.amount, 0) });
  }
  const windows = [30, 60, 90].filter((w) => w <= days).map((w) => {
    const ev = events.filter((e) => daysBetween(start, e.date) <= w);
    const income = round2(ev.filter((e) => e.amount > 0).reduce((s, e) => s + e.amount, 0));
    const bills = round2(-ev.filter((e) => e.amount < 0).reduce((s, e) => s + e.amount, 0));
    const variable = round2(dailyVar * w);
    const pts = series.slice(0, w + 1);
    return { days: w, income, bills, variable, endBalance: series[w].balance, minBalance: Math.min(...pts.map((p) => p.balance)), confirmedIncome: round2(ev.filter((e) => e.confirmed && e.amount > 0).reduce((s, e) => s + e.amount, 0)) };
  });
  return { start, startBalance: bal.total, balanceKnown: bal.known > 0, events, series, windows, shortage, variableMonthly, threshold };
}

export function simulateDebts(debts, extra = 0, strategy = "avalanche") {
  const list = debts.filter((d) => Number(d.balance) > 0).map((d) => ({ id: d.id, name: d.creditor, bal: Number(d.balance), rate: Number(d.rate || 0) / 100 / 12, min: Number(d.minPayment || 0), paidOff: null, interest: 0 }));
  const budget = list.reduce((s, d) => s + d.min, 0) + Number(extra || 0);
  let month = 0;
  let totalInterest = 0;
  const schedule = [{ month: 0, balance: round2(list.reduce((s, d) => s + d.bal, 0)) }];
  while (list.some((d) => d.bal > 0.005) && month < 600) {
    month += 1;
    const startBal = list.reduce((s, d) => s + Math.max(0, d.bal), 0);
    let monthInterest = 0;
    list.forEach((d) => {
      if (d.bal <= 0) return;
      const i = d.bal * d.rate;
      d.bal += i;
      d.interest += i;
      totalInterest += i;
      monthInterest += i;
    });
    let pool = strategy === "minimum" ? 0 : budget;
    list.forEach((d) => {
      if (d.bal <= 0) return;
      const pay = Math.min(d.bal, d.min);
      d.bal -= pay;
      if (strategy !== "minimum") pool -= pay;
    });
    if (strategy !== "minimum") {
      const order = list.filter((d) => d.bal > 0.005).sort((a, b) => (strategy === "snowball" ? a.bal - b.bal : b.rate - a.rate || a.bal - b.bal));
      for (const d of order) {
        if (pool <= 0) break;
        const pay = Math.min(d.bal, pool);
        d.bal -= pay;
        pool -= pay;
      }
    }
    list.forEach((d) => {
      if (d.bal <= 0.005 && d.paidOff === null) {
        d.bal = 0;
        d.paidOff = month;
      }
    });
    const endBal = list.reduce((s, d) => s + Math.max(0, d.bal), 0);
    schedule.push({ month, balance: round2(endBal), interest: round2(monthInterest), principal: round2(startBal - endBal) });
  }
  const neverPaid = list.some((d) => d.bal > 0.005);
  const payoffDate = (m) => {
    const dt = new Date();
    dt.setDate(1);
    dt.setMonth(dt.getMonth() + m);
    return toISO(dt).slice(0, 7);
  };
  return {
    months: neverPaid ? null : month,
    payoffMonth: neverPaid ? null : payoffDate(month),
    totalInterest: round2(totalInterest),
    neverPaid,
    perDebt: list.map((d) => ({ id: d.id, name: d.name, months: d.paidOff, payoffMonth: d.paidOff ? payoffDate(d.paidOff) : null, interest: round2(d.interest) })),
    schedule,
  };
}

export function goalProjection(goal) {
  const remaining = Math.max(0, Number(goal.target || 0) - Number(goal.current || 0));
  const monthly = Number(goal.monthly || 0);
  if (remaining === 0) return { remaining, months: 0, date: null, done: true, pct: 100 };
  const pct = goal.target > 0 ? Math.min(100, (Number(goal.current || 0) / Number(goal.target)) * 100) : 0;
  if (monthly <= 0) return { remaining, months: null, date: null, done: false, pct };
  const months = Math.ceil(remaining / monthly);
  const dt = new Date();
  dt.setDate(1);
  dt.setMonth(dt.getMonth() + months);
  let behind = false;
  if (goal.deadline) behind = toISO(dt).slice(0, 7) > goal.deadline.slice(0, 7);
  return { remaining, months, date: toISO(dt).slice(0, 7), done: false, pct, behind };
}

export function unusualTransactions(state, month) {
  const cats = catMapOf(state);
  const cardMode = cardModeOf(state);
  const byCat = {};
  state.transactions.forEach((t) => {
    if (t.amount < 0 && partKind(t, t, cats, cardMode) === "expense") (byCat[t.category] ||= []).push(-t.amount);
  });
  return state.transactions
    .filter((t) => (!month || monthKey(t.date) === month) && t.amount < 0 && partKind(t, t, cats, cardMode) === "expense")
    .map((t) => {
      const arr = byCat[t.category] || [];
      const mean = arr.reduce((s, a) => s + a, 0) / arr.length;
      const sd = Math.sqrt(arr.reduce((s, a) => s + (a - mean) ** 2, 0) / arr.length);
      const amt = -t.amount;
      let reason = null;
      if (arr.length >= 4 && amt > mean + 2.5 * sd && amt > 100) reason = `About ${round2(amt / mean)}× your typical ${cats[t.category]?.name || ""} charge`;
      else if (amt >= 1000 && !cats[t.category]?.fixed) reason = "Large one-time expense";
      return reason ? { ...t, reason } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.amount - b.amount)
    .slice(0, 10);
}

export function recommendations(state) {
  const months = computeMonths(state);
  const keys = sortedMonths(months);
  const recs = [];
  if (!keys.length) return recs;
  const win = keys.slice(-(state.settings.avgWindow || 3));
  const n = win.length;
  const avgOf = (ids) => round2(win.reduce((s, k) => s + ids.reduce((x, id) => x + (months[k].byCat[id] || 0), 0), 0) / n);
  const label = n === 1 ? "in the month imported" : `over the last ${n} months`;

  const dining = avgOf(["restaurants", "fast-food", "coffee-and-snacks"]);
  if (dining >= 150) {
    const cut = Math.round((dining * 0.25) / 5) * 5;
    recs.push({ id: "dining", severity: "medium", title: "Dining out", text: `Your average spending on restaurants, fast food and coffee is ${money(dining)} per month (${label}). Reducing this by ${money(cut)} could save approximately ${money(cut * 12)} per year.`, saving: cut * 12 });
  }
  const fees = round2(win.reduce((s, k) => s + (months[k].byCat["bank-fees"] || 0), 0));
  if (fees > 0) recs.push({ id: "fees", severity: fees > 50 ? "high" : "low", title: "Bank fees", text: `You paid ${money(fees)} in bank fees ${label} (${money(fees / n)}/month). Ask your bank about fee waivers or a no-fee account — avoiding these would save about ${money((fees / n) * 12)} per year.`, saving: round2((fees / n) * 12) });

  if (keys.length >= 2) {
    const last = keys[keys.length - 1];
    const prior = keys.slice(0, -1).slice(-3);
    state.categories.filter((c) => c.group === "Utilities").forEach((c) => {
      const pAvg = prior.reduce((s, k) => s + (months[k].byCat[c.id] || 0), 0) / prior.length;
      const cur = months[last].byCat[c.id] || 0;
      if (pAvg > 20 && cur > pAvg * 1.15) recs.push({ id: `util-${c.id}`, severity: "medium", title: `Rising ${c.name.toLowerCase()} bill`, text: `${c.name} was ${money(cur)} in the latest month versus a ${money(pAvg)} average before — up ${Math.round(((cur - pAvg) / pAvg) * 100)}%. Check for rate changes or usage spikes.` });
    });
    state.categories.filter((c) => c.type === "expense" && !c.fixed).forEach((c) => {
      const pAvg = prior.reduce((s, k) => s + (months[k].byCat[c.id] || 0), 0) / prior.length;
      const cur = months[last].byCat[c.id] || 0;
      if (cur - pAvg > 150 && cur > pAvg * 1.5 && c.group !== "Utilities") recs.push({ id: `chg-${c.id}`, severity: "low", title: `Large change: ${c.name}`, text: `${c.name} spending was ${money(cur)} in the latest month, compared with an average of ${money(pAvg)} in prior months (+${money(cur - pAvg)}).` });
    });
  }

  const recurring = detectRecurring(state);
  const subs = recurring.filter((r) => r.category === "subscriptions" && !r.isIncome);
  if (subs.length >= 2) {
    const total = round2(subs.reduce((s, r) => s + monthlyEquivalent(r.amount, r.frequency), 0));
    recs.push({ id: "subs", severity: "low", title: "Review subscriptions", text: `We found ${subs.length} recurring subscriptions totaling about ${money(total)} per month (${subs.map((s) => s.name).join(", ")}). If any are no longer used, cancelling them would save that amount each month.` });
  }
  const dupMonth = keys[keys.length - 1];
  const seen = {};
  state.transactions.filter((t) => monthKey(t.date) === dupMonth && t.category === "subscriptions" && t.amount < 0).forEach((t) => {
    const k = `${merchantKey(t.description)}|${t.amount}`;
    seen[k] = (seen[k] || 0) + 1;
  });
  Object.entries(seen).filter(([, c]) => c > 1).forEach(([k]) => recs.push({ id: `dup-${k}`, severity: "medium", title: "Possible duplicate subscription charge", text: `"${merchantName(k.split("|")[0])}" was charged ${money(-Number(k.split("|")[1]))} more than once in ${dupMonth}. Confirm both charges are expected.` }));

  recs.push(...interestAlerts(state));
  recurring.filter((r) => r.increase).forEach((r) => recs.push({ id: `inc-${r.key}`, severity: "medium", title: `Price increase: ${r.name}`, text: `${r.name} went from about ${money(r.increase.from)} to ${money(r.increase.to)} (+${r.increase.pct}%). That is ${money((r.increase.to - r.increase.from) * 12)} more per year if billed monthly.` }));

  const model = budgetModel(state);
  const lastM = keys[keys.length - 1];
  budgetVsActual(state, lastM, model).filter((r) => r.available > 0 && r.actual > r.available).slice(0, 4).forEach((r) => recs.push({ id: `over-${r.id}`, severity: "high", title: `Over budget: ${r.name}`, text: `You spent ${money(r.actual)} on ${r.name} in ${lastM}, ${money(r.actual - r.available)} above the ${money(r.available)} limit.` }));

  const avgIncome = win.reduce((s, k) => s + months[k].income, 0) / n;
  const avgNet = win.reduce((s, k) => s + months[k].net, 0) / n;
  const avgSav = win.reduce((s, k) => s + months[k].savings, 0) / n;
  if (avgIncome > 0 && avgNet > 50) {
    const newRate = ((avgSav + avgNet * 0.5) / avgIncome) * 100;
    recs.push({ id: "save", severity: "low", title: "Opportunity to save more", text: `You have an average surplus of ${money(avgNet)} per month after expenses and savings. Moving half (${money(avgNet / 2)}) to savings automatically would raise your savings rate from ${((avgSav / avgIncome) * 100).toFixed(1)}% to ${newRate.toFixed(1)}%.` });
  } else if (avgIncome > 0 && avgNet < 0) {
    recs.push({ id: "deficit", severity: "high", title: "Spending exceeds income", text: `On average you are spending ${money(-avgNet)} more per month than you bring in (${label}). Review discretionary categories first: they total ${money(win.reduce((s, k) => s + months[k].discretionary, 0) / n)} per month.` });
  }
  return recs;
}

function money(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Math.round(n));
}

export function yearStats(state) {
  const months = computeMonths(state);
  const years = {};
  Object.entries(months).forEach(([k, s]) => {
    const y = k.slice(0, 4);
    const Y = (years[y] ||= { year: y, income: 0, expense: 0, savings: 0, months: 0, byCat: {} });
    Y.income += s.income;
    Y.expense += s.expense;
    Y.savings += s.savings;
    Y.months += 1;
    Object.entries(s.byCat).forEach(([c, v]) => (Y.byCat[c] = (Y.byCat[c] || 0) + v));
  });
  return Object.values(years).sort((a, b) => a.year.localeCompare(b.year));
}

export function irregularMonthly(items = []) {
  return round2(items.reduce((s, i) => s + Number(i.amount || 0) / (Number(i.everyMonths) || 12), 0));
}

export function monthsUntil(monthNum) {
  const now = parseISO(today());
  let diff = Number(monthNum) - (now.getMonth() + 1);
  if (diff <= 0) diff += 12;
  return diff;
}

// Next-payment split for a single debt: interest accrued this month vs principal reduction
export function paymentSplit(debt, payment) {
  const bal = Number(debt.balance || 0);
  const pay = Number(payment ?? debt.minPayment ?? 0);
  const interest = round2((bal * Number(debt.rate || 0)) / 100 / 12);
  const principal = round2(Math.max(0, Math.min(bal, pay - interest)));
  return { payment: round2(pay), interest: round2(Math.min(interest, pay)), principal, pctInterest: pay > 0 ? round2((Math.min(interest, pay) / pay) * 100) : 0, coversInterest: pay > interest, monthlyInterest: interest };
}

// From imported card statements: payments vs interest/fees charged per card per month
export function cardPaymentBreakdown(state) {
  const cards = state.accounts.filter((a) => a.type === "credit");
  const cats = catMapOf(state);
  return cards.map((a) => {
    const months = {};
    state.transactions.filter((t) => t.accountId === a.id).forEach((t) => {
      const m = (months[monthKey(t.date)] ||= { month: monthKey(t.date), payments: 0, interest: 0, fees: 0, purchases: 0 });
      const c = cats[t.category];
      if (t.amount > 0 && (t.category === "credit-card-payments" || c?.type === "transfer" || t.transferPairId)) m.payments += t.amount;
      else if (t.category === "interest-charges") m.interest += -t.amount;
      else if (t.category === "bank-fees") m.fees += -t.amount;
      else if (t.amount < 0) m.purchases += -t.amount;
    });
    const rows = Object.values(months).sort((x, y) => x.month.localeCompare(y.month)).map((m) => {
      const r = { ...m, payments: round2(m.payments), interest: round2(m.interest), fees: round2(m.fees), purchases: round2(m.purchases) };
      r.principal = round2(Math.max(0, r.payments - r.interest - r.fees));
      return r;
    });
    const tot = rows.reduce((s, r) => ({ payments: s.payments + r.payments, interest: s.interest + r.interest, fees: s.fees + r.fees, principal: s.principal + r.principal }), { payments: 0, interest: 0, fees: 0, principal: 0 });
    return { account: a, rows, totals: Object.fromEntries(Object.entries(tot).map(([k, v]) => [k, round2(v)])) };
  });
}

const TRANSFER_WORDS = /(transfer|xfer|trnsfr|to sav|from sav|from chk|to chk|from checking|to checking|card payment|crd pmt|payment thank you|payment received|autopay|epay|online pmt|zelle (to|from) (self|me))/i;

// Pairs an outflow in one account with an equal inflow in another account within a few days
export function findTransferPairs(state, windowDays = 5) {
  const cats = catMapOf(state);
  const acct = Object.fromEntries(state.accounts.map((a) => [a.id, a]));
  const free = (t) => !t.transferPairId && !t.transferUnlinked && !t.excluded && !t.splits?.length && t.date;
  const typeOf = (t) => cats[t.category]?.type;
  const outOk = (t) => t.amount < 0 && free(t) && (TRANSFER_WORDS.test(t.description) || ["transfer", "savings"].includes(typeOf(t)) || ["credit-card-payments", "unclassified-transactions"].includes(t.category));
  const inOk = (t) => t.amount > 0 && free(t) && (TRANSFER_WORDS.test(t.description) || typeOf(t) === "transfer" || ["credit-card-payments", "other-income", "unclassified-transactions"].includes(t.category));
  const strong = (t) => TRANSFER_WORDS.test(t.description) || ["transfer", "savings"].includes(typeOf(t)) || t.category === "credit-card-payments";
  const ins = state.transactions.filter(inOk);
  const used = new Set();
  const pairs = [];
  state.transactions.filter(outOk).sort((a, b) => a.date.localeCompare(b.date)).forEach((o) => {
    const cands = ins.filter((i) => !used.has(i.id) && i.accountId !== o.accountId && round2(i.amount) === round2(-o.amount) && Math.abs(daysBetween(o.date, i.date)) <= windowDays && (strong(o) || strong(i) || acct[i.accountId]?.type === "credit"));
    if (!cands.length) return;
    const i = cands.sort((a, b) => Math.abs(daysBetween(o.date, a.date)) - Math.abs(daysBetween(o.date, b.date)))[0];
    used.add(i.id);
    const destType = acct[i.accountId]?.type;
    const outCategory = destType === "credit" ? "credit-card-payments" : destType === "savings" ? (typeOf(o) === "savings" ? o.category : "other-savings") : "transfer-between-accounts";
    const inCategory = destType === "credit" ? "credit-card-payments" : "transfer-between-accounts";
    pairs.push({ outId: o.id, inId: i.id, amount: round2(i.amount), outCategory, inCategory, fromAccount: o.accountId, toAccount: i.accountId, date: o.date });
  });
  return pairs;
}

// Mutates a state draft; respects categories the user set by hand
export function applyTransferPairs(draft, pairs) {
  const byId = Object.fromEntries(draft.transactions.map((t) => [t.id, t]));
  pairs.forEach((p) => {
    const o = byId[p.outId];
    const i = byId[p.inId];
    if (!o || !i) return;
    const pid = `${p.outId}~${p.inId}`;
    o.transferPairId = pid;
    i.transferPairId = pid;
    if (o.autoCategory !== false) o.category = p.outCategory;
    if (i.autoCategory !== false) i.category = p.inCategory;
    if (o.flagged && /category/i.test(o.flagReason || "")) o.flagged = false;
    if (i.flagged && /category/i.test(i.flagReason || "")) i.flagged = false;
  });
  return pairs.length;
}

// Calendar for one month: actual activity on past days, expected bills/paydays from today on
export function calendarMonth(state, mk) {
  const [y, m] = mk.split("-").map(Number);
  const first = `${mk}-01`;
  const last = toISO(new Date(y, m, 0));
  const t = today();
  const cats = catMapOf(state);
  const cardMode = cardModeOf(state);
  const { events } = projectRecurring(state, t > first ? t : first, last);
  const fc = forecast(state, 90);
  const bal = Object.fromEntries(fc.series.map((p) => [p.date, p.balance]));
  const days = [];
  for (let d = 1; d <= Number(last.slice(8)); d++) {
    const date = `${mk}-${String(d).padStart(2, "0")}`;
    const txs = state.transactions.filter((x) => x.date === date && !["transfer", "excluded"].includes(partKind(x, x, cats, cardMode)));
    const expected = date >= t ? events.filter((e) => e.date === date) : [];
    days.push({
      date,
      isPast: date < t,
      isToday: date === t,
      actualIn: round2(txs.filter((x) => x.amount > 0).reduce((s, x) => s + x.amount, 0)),
      actualOut: round2(-txs.filter((x) => x.amount < 0).reduce((s, x) => s + x.amount, 0)),
      txs,
      expected,
      balance: bal[date] ?? null,
      low: bal[date] !== undefined && bal[date] < fc.threshold,
    });
  }
  const exp = days.flatMap((d) => d.expected);
  return {
    days,
    firstWeekday: new Date(y, m - 1, 1).getDay(),
    totals: { expectedIncome: round2(exp.filter((e) => e.amount > 0).reduce((s, e) => s + e.amount, 0)), expectedBills: round2(-exp.filter((e) => e.amount < 0).reduce((s, e) => s + e.amount, 0)), actualIn: round2(days.reduce((s, d) => s + d.actualIn, 0)), actualOut: round2(days.reduce((s, d) => s + d.actualOut, 0)) },
  };
}

// Interest warnings from card statements and entered debts
export function interestAlerts(state) {
  const out = [];
  const money2 = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
  cardPaymentBreakdown(state).forEach(({ account, rows }) => {
    const withInt = rows.filter((r) => r.interest > 0);
    if (withInt.length >= 2) {
      const [prev, last] = withInt.slice(-2);
      if (last.interest > prev.interest * 1.1 && last.interest - prev.interest >= 2)
        out.push({ id: `int-up-${account.id}-${last.month}`, severity: "medium", kind: "interest", title: `Interest rising on ${account.name}`, text: `Interest charged went from ${money2(prev.interest)} (${prev.month}) to ${money2(last.interest)} (${last.month}), up ${Math.round(((last.interest - prev.interest) / prev.interest) * 100)}%. Paying more than the minimum, or paying before the due date, lowers it.` });
    }
    const last = rows[rows.length - 1];
    if (last && last.interest > 0 && last.payments > 0 && last.interest / last.payments >= 0.5)
      out.push({ id: `int-cover-${account.id}-${last.month}`, severity: "high", kind: "interest", title: `Payments barely beat interest on ${account.name}`, text: `In ${last.month} you paid ${money2(last.payments)} and ${money2(last.interest)} of it (${Math.round((last.interest / last.payments) * 100)}%) went to interest — only ${money2(last.principal)} reduced what you owe.` });
  });
  state.debts.forEach((d) => {
    if (!(Number(d.balance) > 0) || !(Number(d.rate) > 0)) return;
    const sp = paymentSplit(d);
    if (!sp.coversInterest)
      out.push({ id: `debt-nocover-${d.id}`, severity: "high", kind: "interest", title: `${d.creditor}: payment doesn't cover interest`, text: `About ${money2(sp.monthlyInterest)} of interest builds up each month but the minimum payment is ${money2(sp.payment)}, so the balance will grow. Paying at least ${money2(Math.ceil(sp.monthlyInterest * 1.25))} would start reducing it.` });
    else if (sp.pctInterest >= 75)
      out.push({ id: `debt-barely-${d.id}`, severity: sp.pctInterest >= 90 ? "high" : "medium", kind: "interest", title: `${d.creditor}: payment barely covers interest`, text: `${sp.pctInterest}% of your ${money2(sp.payment)} minimum payment is interest (${money2(sp.interest)}); only ${money2(sp.principal)} lowers the balance each month.` });
    const h = (d.rateHistory || []).slice(-1)[0];
    if (h && Number(h.to) > Number(h.from))
      out.push({ id: `debt-apr-${d.id}-${h.date}`, severity: "medium", kind: "interest", title: `${d.creditor}: rate increased`, text: `You updated the APR from ${h.from}% to ${h.to}% on ${h.date}. On a ${money2(Number(d.balance))} balance that's about ${money2(((Number(h.to) - Number(h.from)) / 100 / 12) * Number(d.balance))} more interest per month.` });
  });
  const dismissed = new Set(state.dismissedAlerts || []);
  return out.filter((a) => !dismissed.has(a.id));
}

// Summary of everything imported for a calendar year
export function importYearReport(state, year) {
  const y = String(year);
  const inYear = (d) => (d || "").startsWith(y);
  const statements = state.statements.filter((s) => inYear(s.period?.end || s.importedAt?.slice(0, 10)) || state.transactions.some((t) => t.statementId === s.id && inYear(t.date)));
  const txs = state.transactions.filter((t) => inYear(t.date));
  const months = computeMonths({ ...state, transactions: txs });
  const keys = sortedMonths(months);
  const sum = (k) => round2(keys.reduce((a, m) => a + months[m][k], 0));
  const pairIds = new Set(txs.filter((t) => t.transferPairId).map((t) => t.transferPairId));
  const pairs = [...pairIds].map((pid) => state.transactions.filter((t) => t.transferPairId === pid));
  const accounts = state.accounts.map((a) => {
    const mset = new Set(txs.filter((t) => t.accountId === a.id).map((t) => t.date.slice(0, 7)));
    const covered = Array.from({ length: 12 }, (_, i) => mset.has(`${y}-${String(i + 1).padStart(2, "0")}`));
    const firstIdx = covered.indexOf(true);
    const lastIdx = covered.lastIndexOf(true);
    const gaps = firstIdx < 0 ? [] : covered.map((c, i) => (!c && i > firstIdx && i < lastIdx ? `${y}-${String(i + 1).padStart(2, "0")}` : null)).filter(Boolean);
    return { id: a.id, name: a.name, type: a.type, covered, gaps, count: txs.filter((t) => t.accountId === a.id).length };
  }).filter((a) => a.count);
  const flagged = txs.filter((t) => t.flagged);
  const unclassified = txs.filter((t) => t.category === "unclassified-transactions" && !t.splits?.length);
  const unreconciled = statements.filter((s) => s.reconciliation && !s.reconciliation.ok);
  return {
    year: y,
    statements: statements.map((s) => ({ ...s, accountName: state.accounts.find((a) => a.id === s.accountId)?.name || "" })),
    totals: { income: sum("income"), expense: sum("expense"), savings: sum("savings"), net: sum("net"), transactions: txs.length, months: keys.length, duplicatesSkipped: statements.reduce((a, s) => a + (s.skipped || 0), 0) },
    transfers: { count: pairs.length, amount: round2(pairs.reduce((a, p) => a + Math.abs(p[0]?.amount || 0), 0)), list: pairs.slice(0, 50).map((p) => { const o = p.find((t) => t.amount < 0) || p[0]; const i = p.find((t) => t.amount > 0) || p[1]; return { id: o.transferPairId, date: o.date, amount: Math.abs(o.amount), from: state.accounts.find((a) => a.id === o.accountId)?.name, to: state.accounts.find((a) => a.id === i?.accountId)?.name, outId: o.id, inId: i?.id }; }) },
    accounts,
    review: { flagged, unclassified, unreconciled, gapCount: accounts.reduce((a, x) => a + x.gaps.length, 0) },
  };
}

// Year-end tax summary: business expenses, charitable donations, medical costs (from data only)
export function taxPack(state, year) {
  const y = String(year);
  const cats = catMapOf(state);
  const acc = Object.fromEntries(state.accounts.map((a) => [a.id, a.name]));
  const sections = { business: [], charity: [], medical: [] };
  state.transactions.filter((t) => t.date?.startsWith(y) && !t.excluded).forEach((t) => {
    txParts(t).forEach((p) => {
      const c = cats[p.category];
      const row = { id: t.id, date: t.date, description: t.description, category: c?.name || p.category, amount: round2(-p.amount), account: acc[t.accountId] || "", notes: t.notes || "", split: !!t.splits?.length, receipts: (t.receipts || []).length };
      if (t.business && c?.type === "expense") sections.business.push(row);
      if (p.category === "charitable-contributions") sections.charity.push(row);
      if (c?.group === "Healthcare") sections.medical.push(row);
    });
  });
  const summarize = (rows) => {
    const byCat = {};
    rows.forEach((r) => (byCat[r.category] = round2((byCat[r.category] || 0) + r.amount)));
    return { rows: rows.sort((a, b) => a.date.localeCompare(b.date)), total: round2(rows.reduce((s, r) => s + r.amount, 0)), byCat: Object.entries(byCat).sort((a, b) => b[1] - a[1]), missingNotes: rows.filter((r) => !r.notes).length };
  };
  return { year: y, business: summarize(sections.business), charity: summarize(sections.charity), medical: summarize(sections.medical), mileage: mileageSummary(state, y), months: new Set(state.transactions.filter((t) => t.date?.startsWith(y)).map((t) => t.date.slice(0, 7))).size };
}

export function mileageSummary(state, year) {
  const rate = Number(state.settings?.mileageRate ?? 0.7);
  const rows = (state.mileage || []).filter((m) => m.date?.startsWith(String(year))).sort((a, b) => a.date.localeCompare(b.date));
  const miles = round2(rows.reduce((s, m) => s + Number(m.miles || 0), 0));
  return { rows, miles, rate, deduction: round2(miles * rate) };
}

// Upcoming bill payments (not income) due within `days` days from today
export function upcomingBills(state, days) {
  const start = today();
  const dismissed = new Set(state.dismissedReminders || []);
  const cats = catMapOf(state);
  return projectRecurring(state, start, addDays(start, Math.max(0, Number(days) || 0))).events
    .filter((e) => !e.isIncome && !["savings", "transfer"].includes(cats[e.category]?.type) && e.category !== "cash-withdrawals")
    .map((e) => ({ ...e, key: `${e.itemKey}|${e.date}`, inDays: daysBetween(start, e.date) }))
    .filter((e) => !dismissed.has(e.key));
}

// Estimated interest avoided when a debt is paid off, vs. continuing minimum payments from its baseline
export function payoffSavings(debt, payoffDate = today()) {
  const base = debt.baseline || { date: payoffDate, balance: debt.balance };
  const sim = simulateDebts([{ id: "x", balance: base.balance, rate: debt.rate, minPayment: debt.minPayment }], 0, "minimum");
  const [y1, m1] = base.date.split("-").map(Number);
  const [y2, m2] = payoffDate.split("-").map(Number);
  const elapsed = Math.max(0, (y2 - y1) * 12 + (m2 - m1));
  const paidSoFar = sim.schedule.slice(1, elapsed + 1).reduce((s, x) => s + x.interest, 0);
  return { interestSaved: sim.neverPaid ? null : round2(Math.max(0, sim.totalInterest - paidSoFar)), monthsEarly: sim.neverPaid ? null : Math.max(0, sim.months - elapsed), neverPaid: sim.neverPaid };
}

// Highest known balance: baseline when added, or the balance before the first logged payment
export const startBalance = (d) => {
  const p = d.payments?.[0];
  return Math.max(Number(d.baseline?.balance || 0), p ? Number(p.balanceAfter) + Number(p.amount || 0) : 0, Number(d.balance || 0));
};

export const paidPct = (d) => {
  const start = startBalance(d);
  return start > 0 ? ((start - Number(d.balance)) / start) * 100 : 0;
};

export const MILESTONES = [25, 50, 75];

// Highest milestone newly reached when the balance moves from `before` to `after`
export function crossedMilestone(debt, before, after) {
  const start = startBalance(debt);
  if (!(start > 0) || after <= 0) return null;
  const p0 = ((start - before) / start) * 100;
  const p1 = ((start - after) / start) * 100;
  const hit = MILESTONES.filter((m) => p0 < m && p1 >= m && !(debt.milestones || []).includes(m));
  return hit.length ? hit[hit.length - 1] : null;
}

// Every milestone newly reached by this change (a big payment can pass several)
export function crossedMilestones(debt, before, after) {
  const start = startBalance(debt);
  if (!(start > 0)) return [];
  const p0 = ((start - before) / start) * 100;
  const p1 = ((start - Math.max(0, after)) / start) * 100;
  return MILESTONES.filter((m) => p0 < m && p1 >= m && !(debt.milestones || []).includes(m));
}

// 25/50/75/100% with the date reached; "earlier" when reached before dates were recorded
export function milestoneTimeline(debt) {
  const pct = paidPct(debt);
  const dates = debt.milestoneDates || {};
  return [...MILESTONES, 100].map((m) => {
    const date = m === 100 ? debt.paidOff?.date || null : dates[m] || null;
    const reached = m === 100 ? !!debt.paidOff && Number(debt.balance) === 0 : !!date || (debt.milestones || []).includes(m) || pct >= m;
    return { pct: m, date, reached };
  });
}

// Business expenses matching a saved trip's match words, not yet logged or dismissed
export function tripSuggestions(state, year) {
  const tpls = (state.tripTemplates || []).map((t) => ({ ...t, words: String(t.matchWords || "").toLowerCase().split(/[,;]+/).map((w) => w.trim()).filter(Boolean) })).filter((t) => t.words.length);
  if (!tpls.length) return [];
  const logged = new Set((state.mileage || []).filter((m) => m.txId).map((m) => `${m.templateId}|${m.txId}`));
  const dismissed = new Set(state.dismissedTripSuggestions || []);
  const out = [];
  state.transactions.filter((t) => t.business && t.amount < 0 && !t.excluded && t.date?.startsWith(String(year))).forEach((t) => {
    const desc = `${t.description} ${t.notes || ""}`.toLowerCase();
    const tpl = tpls.find((p) => p.words.some((w) => desc.includes(w)));
    if (!tpl) return;
    const key = `${tpl.id}|${t.id}`;
    if (!logged.has(key) && !dismissed.has(key)) out.push({ key, tx: t, template: tpl });
  });
  return out.sort((a, b) => b.tx.date.localeCompare(a.tx.date));
}

// Business expenses older than `days` with no receipt attached
export const staleMissingReceipts = (state, days = 7) => {
  const cutoff = addDays(today(), -days);
  return state.transactions.filter((t) => t.business && t.amount < 0 && !t.excluded && t.date <= cutoff && !(t.receipts || []).length);
};

// Miles per month (12 buckets) and per frequent trip for a year
export function mileageInsights(state, year) {
  const rows = (state.mileage || []).filter((m) => m.date?.startsWith(String(year)));
  const tpls = state.tripTemplates || [];
  const months = Array.from({ length: 12 }, (_, i) => ({ month: new Date(2000, i, 1).toLocaleString(undefined, { month: "short" }), miles: 0 }));
  const by = {};
  rows.forEach((m) => {
    months[Number(m.date.slice(5, 7)) - 1].miles += Number(m.miles || 0);
    const tpl = tpls.find((t) => t.id === m.templateId) || tpls.find((t) => t.purpose.toLowerCase() === String(m.purpose || "").toLowerCase());
    const k = tpl ? tpl.id : "_other";
    by[k] = by[k] || { id: k, name: tpl ? tpl.purpose : "One-off trips", trips: 0, miles: 0 };
    by[k].trips += 1;
    by[k].miles += Number(m.miles || 0);
  });
  months.forEach((m) => (m.miles = round2(m.miles)));
  return { months, byTrip: Object.values(by).map((b) => ({ ...b, miles: round2(b.miles) })).sort((a, b) => b.miles - a.miles) };
}

// Goal milestones newly crossed when savings move from `before` to `after`
export function goalMilestones(goal, before, after) {
  const t = Number(goal.target || 0);
  if (!(t > 0)) return [];
  return [...MILESTONES, 100].filter((m) => (before / t) * 100 < m && (after / t) * 100 >= m && !(goal.milestones || []).includes(m));
}

export function goalTimeline(goal) {
  const t = Number(goal.target || 0);
  const pct = t > 0 ? (Number(goal.current || 0) / t) * 100 : 0;
  const dates = goal.milestoneDates || {};
  return [...MILESTONES, 100].map((m) => ({ pct: m, date: dates[m] || null, reached: !!dates[m] || (goal.milestones || []).includes(m) || pct >= m }));
}

// Tax season reminder: Dec 1 – Apr 15 covers the year ending / just ended
export function yearEndStatus(state, asOf = today()) {
  const y = Number(asOf.slice(0, 4));
  const md = asOf.slice(5);
  const year = md >= "12-01" ? y : md <= "04-15" ? y - 1 : null;
  if (!year || state.yearEndDismissed === String(year)) return null;
  const missing = state.transactions.filter((t) => t.business && t.amount < 0 && !t.excluded && t.date?.startsWith(String(year)) && !(t.receipts || []).length);
  const trips = tripSuggestions(state, year);
  const checklist = taxChecklist(state, year);
  if (checklist.done === checklist.total) return null;
  return { year: String(year), missing, trips, checklist, deadline: `${md >= "12-01" ? y + 1 : y}-04-15` };
}

// Tax season checklist: auto items come from data, manual ones are ticked and stored per year
export function taxChecklist(state, year) {
  const y = String(year);
  const marks = (state.taxChecklist || {})[y] || {};
  const biz = state.transactions.filter((t) => t.business && t.amount < 0 && !t.excluded && t.date?.startsWith(y));
  const missing = biz.filter((t) => !(t.receipts || []).length).length;
  const trips = tripSuggestions(state, y).length;
  const items = [
    { id: "receipts", label: "Attach receipts to business expenses", auto: true, done: missing === 0, detail: biz.length ? (missing ? `${missing} of ${biz.length} still missing` : `All ${biz.length} attached`) : "No business expenses" },
    { id: "trips", label: "Log suggested business trips", auto: true, done: trips === 0, detail: trips ? `${trips} suggestion(s) waiting` : "Nothing waiting" },
    { id: "rate", label: `Check the mileage rate (${rateLabel(state.settings?.mileageRate ?? 0.7)}/mi)`, done: !!marks.rate, date: marks.rate },
    { id: "reviewed", label: "Review business, charity and medical items", done: !!marks.reviewed, date: marks.reviewed },
    { id: "exported", label: "Export the tax pack (PDF, Excel or CSV)", done: !!marks.exported, date: marks.exported },
    { id: "sent", label: "Send it to my accountant / tax software", done: !!marks.sent, date: marks.sent },
  ];
  return { items, done: items.filter((i) => i.done).length, total: items.length };
}
